/* eslint-disable react-hooks/rules-of-hooks, no-console */
import React, {useRef, useState, useEffect, useCallback, useMemo} from 'react';
import {Stage, Layer, Line, Shape} from 'react-konva';
import ComponentRenderer from './canvas/CanvasComponents';
import ContextMenu from './canvas/ContextMenu';
import Grid from './canvas/Grid';
import useCanvasHandlers from './canvas/useCanvasHandlers';
import Cookies from 'js-cookie';
import debounce from 'lodash.debounce';
import AIAssistant from './AIAssistant';
import i18n from "../../i18n";
import { useTranslation } from 'react-i18next';

// Размеры иконок
const iconWidth = 50;
const iconHeight = 50;

const currentLanguage = i18n.language;

const SystemComponent = ({name, x, y, width, height, onClick}) => {
    return (
        <Shape
            x={x}
            y={y}
            sceneFunc={(context, shape) => {
                context.beginPath();

                // Прямоугольник с белой заливкой
                context.rect(0, 0, width, height);
                context.fillStyle = 'white';
                context.fill();

                // Оранжевая обводка
                context.strokeStyle = '#FF6F00';
                context.lineWidth = 2;
                context.stroke();

                // Текст названия системы
                context.fillStyle = '#333333';
                context.font = 'bold 16px Arial';
                context.textAlign = 'center';
                context.textBaseline = 'middle';
                context.fillText(name, width / 2, height / 2);

                context.fillStrokeShape(shape);
            }}
            onMouseEnter={(e) => {
                const stage = e.target.getStage();
                stage.container().style.cursor = 'pointer';
            }}
            onMouseLeave={(e) => {
                const stage = e.target.getStage();
                stage.container().style.cursor = 'default';
            }}
            onClick={onClick}
            onTap={onClick}
        />
    );
};


// Распределение компонентов по уровням
const distributeComponentsByLevels = (components, canvasWidth, canvasHeight) => {
    // Добавляем проверку на минимальные размеры холста
    const minCanvasWidth = 800;
    const minCanvasHeight = 600;
    const effectiveWidth = Math.max(canvasWidth || minCanvasWidth, minCanvasWidth);
    const effectiveHeight = Math.max(canvasHeight || minCanvasHeight, minCanvasHeight);
    
    // Добавляем отступы от краёв и панелей
    const leftPadding = 300; // отступ от левой панели компонентов
    const topPadding = 100;  // отступ сверху
    const rightPadding = 100; // отступ справа
    const bottomPadding = 150; // отступ снизу для AI ассистента
    
    const usableWidth = effectiveWidth - leftPadding - rightPadding;
    const usableHeight = effectiveHeight - topPadding - bottomPadding;
    
    const levels = {};

    // Группируем компоненты по уровням
    components.forEach((comp) => {
        if (!levels[comp.type_level]) {
            levels[comp.type_level] = [];
        }
        levels[comp.type_level].push(comp);
    });

    const placedComponents = [];
    const levelKeys = Object.keys(levels).sort((a, b) => parseInt(a) - parseInt(b));

    // Определяем вертикальный интервал между уровнями
    const levelOffset = Math.min(150, usableHeight / (levelKeys.length + 1));

    // Перебираем уровни
    levelKeys.forEach((level, levelIndex) => {
        const levelComponents = levels[level];

        // Определяем горизонтальный интервал между компонентами
        const componentWidth = iconWidth + 60; // Ширина компонента с отступом
        const totalComponentsWidth = levelComponents.length * componentWidth;

        // Проверяем, поместятся ли все компоненты на экране
        let spacing;

        if (totalComponentsWidth > usableWidth) {
            // Если не помещаются, уменьшаем интервал между ними
            spacing = Math.max(20, (usableWidth - (levelComponents.length * iconWidth)) / (levelComponents.length + 1));
        } else {
            // Если помещаются, распределяем их равномерно
            spacing = (usableWidth - totalComponentsWidth) / (levelComponents.length + 1);
        }

        // Расставляем компоненты с учетом вычисленного интервала
        levelComponents.forEach((comp, index) => {
            const x = leftPadding + spacing + index * (iconWidth + spacing);
            const y = topPadding + (levelIndex + 1) * levelOffset;

            placedComponents.push({
                ...comp,
                x: Math.max(leftPadding, x), // Убеждаемся, что x не меньше отступа
                y: Math.max(topPadding, y),   // Убеждаемся, что y не меньше отступа
                width: iconWidth,
                height: iconHeight,
            });
        });
    });

    return placedComponents;
};

const SandboxCanvas = ({
                           components,
                           setComponents,
                           setActiveComponent,
                           relations,
                           containerRef,
                           userInfo,
                           currentLayer,
                           sandboxName,
                           onLayerChange,
                           token,
                           sandboxId,
                           activeComponent,
                           activeInfoTab,
                           aiTutor = null
                       }) => {
    const { t } = useTranslation();
    const stageRef = useRef(null);

    const [dimensions, setDimensions] = useState({width: 0, height: 0});
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({x: 0, y: 0});
    const [contextMenu, setContextMenu] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [aiAssistantPrompt, setAiAssistantPrompt] = useState(null);

    // Состояние для всплывающего сообщения
    const [tooltip, setTooltip] = useState({
        visible: false,
        x: 0,
        y: 0,
        description: '',
    });
    
    // В самом начале функции SandboxCanvas, после всех useState
    const [renderError, setRenderError] = useState(null);

    // Добавляем обработчик ошибок
    useEffect(() => {
        const handleError = (error) => {
            console.error('Canvas render error:', error);
            setRenderError(error.message || t('sandbox_canvas.errors.render_error'));
        };

        window.addEventListener('error', handleError);
        return () => window.removeEventListener('error', handleError);
    }, [t]);

    // Перед проверкой размеров добавьте:
    if (renderError) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-red-500 p-4 bg-red-50 rounded">
                    {t('sandbox_canvas.errors.display_error')}: {renderError}
                    <button
                        onClick={() => window.location.reload()}
                        className="ml-4 px-3 py-1 bg-red-500 text-white rounded"
                    >
                        {t('sandbox_canvas.buttons.reload')}
                    </button>
                </div>
            </div>
        );
    }

    useCanvasHandlers({
        containerRef,
        stageRef,
        dimensions,
        setDimensions,
        setScale,
        setPosition,
        setContextMenu,
        components,
        setComponents,
        currentLayer,
        sandboxName,
        onLayerChange,
    });

    // Функция для сохранения позиций компонентов в куки (с дебаунсом)
    const debouncedUpdateComponents = useMemo(() => {
        return debounce((updatedComponents) => {
            // Проверка валидности координат
            const isDataValid = updatedComponents.every(
                (comp) =>
                    typeof comp.x === 'number' &&
                    typeof comp.y === 'number' &&
                    !isNaN(comp.x) &&
                    !isNaN(comp.y) &&
                    comp.x >= 0 && 
                    comp.y >= 0 &&
                    comp.x < 10000 && // Разумные максимальные значения
                    comp.y < 10000
            );

            if (!isDataValid) {
                console.warn(t('sandbox_canvas.warnings.invalid_coordinates'));
                return;
            }

            // Подготовка данных для сохранения
            const componentsData = updatedComponents.map((comp) => ({
                id: comp.id,
                x: Math.round(comp.x), // Округляем до целых чисел
                y: Math.round(comp.y),
            }));

            // Сохранение в cookies
            try {
                const dataString = JSON.stringify(componentsData);
                // Проверяем размер данных (cookies имеют ограничение)
                if (dataString.length < 4000) { // 4KB - безопасный предел для cookies
                    Cookies.set('sandboxComponents', dataString, { expires: 30 });
                } else {
                    console.warn(t('sandbox_canvas.warnings.data_too_large'));
                }
            } catch (error) {
                console.error(t('sandbox_canvas.errors.component_save_error'), error);
            }
        }, 300);
    }, [t]);

    // Функция для загрузки позиций компонентов из куки
    const loadComponentsFromCookies = useCallback(() => {
        try {
            const savedComponents = Cookies.get('sandboxComponents');
            if (!savedComponents) return null;
            
            const parsed = JSON.parse(savedComponents);
            
            // Проверяем, что это массив и он не пустой
            if (!Array.isArray(parsed) || parsed.length === 0) {
                Cookies.remove('sandboxComponents'); // Удаляем невалидные данные
                return null;
            }
            
            // Проверяем валидность каждого компонента
            const isValid = parsed.every(comp => 
                comp && 
                typeof comp.id !== 'undefined' &&
                typeof comp.x === 'number' && 
                typeof comp.y === 'number' &&
                !isNaN(comp.x) && 
                !isNaN(comp.y)
            );
            
            if (!isValid) {
                Cookies.remove('sandboxComponents'); // Удаляем невалидные данные
                return null;
            }
            
            return parsed;
        } catch (error) {
            console.error(t('sandbox_canvas.errors.component_load_error'), error);
            Cookies.remove('sandboxComponents'); // Удаляем поврежденные данные
            return null;
        }
    }, [t]);

    // Используем ResizeObserver для обновления размеров контейнера
    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            if (entries && entries.length > 0) {
                const {width, height} = entries[0].contentRect;
                if (width > 0 && height > 0) {
                    setDimensions({width, height});
                }
            }
        });

        resizeObserver.observe(containerRef.current);

        // Очистка при размонтировании
        return () => {
            resizeObserver.disconnect();
        };
    }, [containerRef]);


    // Первичная загрузка компонентов и их распределение по сетке или из куки
    useEffect(() => {
        if (!isInitialized && dimensions.width > 0 && dimensions.height > 0 && components.length > 0) {
            const savedComponents = loadComponentsFromCookies();

            setComponents((prevComponents) => {
                let updatedComponents;

                if (savedComponents && savedComponents.length === prevComponents.length) {
                    // Минимальные отступы для проверки валидности
                    const minX = 300; // отступ от левой панели
                    const minY = 100; // отступ сверху
                    const maxX = dimensions.width - iconWidth - 100;
                    const maxY = dimensions.height - iconHeight - 150;

                    // Проверяем валидность координат сохранённых компонентов
                    const allValidPositions = savedComponents.every(
                        (comp) => typeof comp.x === 'number' &&
                            typeof comp.y === 'number' &&
                            comp.x >= minX &&
                            comp.y >= minY &&
                            comp.x <= maxX &&
                            comp.y <= maxY
                    );

                    if (allValidPositions) {
                        // Обновляем позиции компонентов из сохраненных данных
                        updatedComponents = prevComponents.map((comp) => {
                            const savedComp = savedComponents.find((sc) => sc.id === comp.id);
                            return savedComp
                                ? {...comp, x: savedComp.x, y: savedComp.y}
                                : comp;
                        });
                    } else {
                        // Если позиции невалидны, распределяем заново
                        // Using console.warn instead of console.log for warning messages
                        console.warn(t('sandbox_canvas.warnings.invalid_positions'));
                        Cookies.remove('sandboxComponents'); // Удаляем невалидные данные
                        updatedComponents = distributeComponentsByLevels(
                            prevComponents,
                            dimensions.width,
                            dimensions.height
                        );
                    }
                } else {
                    // Распределяем компоненты по уровням, если нет сохраненных данных или их количество не совпадает
                    updatedComponents = distributeComponentsByLevels(
                        prevComponents,
                        dimensions.width,
                        dimensions.height
                    );
                }

                return updatedComponents;
            });

            setIsInitialized(true);
        }
    }, [dimensions.width, dimensions.height, setComponents, components, isInitialized, loadComponentsFromCookies, t]);

    // Сохраняем компоненты в cookies при их изменении
    useEffect(() => {
        if (isInitialized && components.length > 0) {
            debouncedUpdateComponents(components);
        }
    }, [components, debouncedUpdateComponents, isInitialized]);

    const handleContextMenu = useCallback((e, comp) => {
        e.evt.preventDefault();
        if (!stageRef.current) return;
        const pointer = stageRef.current.getPointerPosition();
        if (!pointer) return;
        setContextMenu({
            x: pointer.x,
            y: pointer.y,
            componentId: comp.id,
            componentName: comp.name
        });
    }, []);



    const handleComponentClick = useCallback(
        (comp) => {
            // Убираем проверку на assignmentMode
            setActiveComponent((prevActiveComponent) => {
                if (prevActiveComponent && prevActiveComponent.id === comp.id) {
                    return null;
                }
                return comp;
            });
        },
        [setActiveComponent]
    );

    const [dashOffset, setDashOffset] = useState(0);
    useEffect(() => {
        const anim = setInterval(() => {
            setDashOffset((prevOffset) => (prevOffset + 1) % 10);
        }, 100);
        return () => clearInterval(anim);
    }, []);

    // Функции для работы с всплывающим сообщением
    const handleMouseEnter = useCallback((e, relation) => {
        if (!stageRef.current) return;
        const pointerPosition = stageRef.current.getPointerPosition();
        if (!pointerPosition) return;

        setTooltip({
            visible: true,
            x: pointerPosition.x + 10,
            y: pointerPosition.y + 10,
            description: relation.description || t('sandbox_canvas.tooltips.no_description'),
        });
    }, [t]);

    const handleMouseLeave = useCallback(() => {
        setTooltip({visible: false, x: 0, y: 0, description: ''});
    }, []);

    const handleSystemClick = useCallback(() => {
        onLayerChange('Подробная архитектура');
    }, [onLayerChange]);

    const preventComponentOverlap = (components, draggedId, newX, newY, dimensions) => {
        // Размеры компонента с отступами
        const componentWidth = iconWidth + 20;
        const componentHeight = iconHeight + 40;
        const minDistance = 10; // Минимальное расстояние между компонентами

        // Ограничиваем позицию границами холста с учетом панелей
        const leftBound = 300; // отступ от левой панели
        const topBound = 50;
        const rightBound = dimensions.width - componentWidth - 50;
        const bottomBound = dimensions.height - componentHeight - 150;

        // Сначала ограничиваем границами
        newX = Math.max(leftBound, Math.min(newX, rightBound));
        newY = Math.max(topBound, Math.min(newY, bottomBound));

        // Находим все компоненты, кроме текущего
        const otherComponents = components.filter(comp => comp.id !== draggedId);

        // Проверяем наличие перекрытий и находим ближайшую свободную позицию
        let hasOverlap = true;
        let attempts = 0;
        const maxAttempts = 10;

        while (hasOverlap && attempts < maxAttempts) {
            hasOverlap = false;
            attempts++;

            for (const comp of otherComponents) {
                // Проверяем перекрытие
                if (
                    newX < comp.x + componentWidth + minDistance &&
                    newX + componentWidth + minDistance > comp.x &&
                    newY < comp.y + componentHeight + minDistance &&
                    newY + componentHeight + minDistance > comp.y
                ) {
                    hasOverlap = true;
                    
                    // Вычисляем направление для смещения
                    const centerX = newX + componentWidth / 2;
                    const centerY = newY + componentHeight / 2;
                    const compCenterX = comp.x + componentWidth / 2;
                    const compCenterY = comp.y + componentHeight / 2;
                    
                    const dx = centerX - compCenterX;
                    const dy = centerY - compCenterY;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    
                    if (distance > 0) {
                        // Смещаем в направлении от центра другого компонента
                        const moveDistance = componentWidth + minDistance;
                        newX += (dx / distance) * moveDistance * 0.1;
                        newY += (dy / distance) * moveDistance * 0.1;
                    } else {
                        // Если центры совпадают, смещаем вправо-вниз
                        newX += componentWidth + minDistance;
                        newY += minDistance;
                    }
                    
                    // Снова ограничиваем границами
                    newX = Math.max(leftBound, Math.min(newX, rightBound));
                    newY = Math.max(topBound, Math.min(newY, bottomBound));
                    
                    break; // Проверяем заново со всеми компонентами
                }
            }
        }

        return { x: Math.round(newX), y: Math.round(newY) };
    };

    const handleDragMove = useCallback((e, id) => {
        const node = e.target;
        const { x, y } = node.position();
        
        // Временно отключаем анимацию для плавного перетаскивания
        node.to({
            duration: 0,
            x: x,
            y: y
        });
        
        // Обновляем позицию с проверкой на наложения
        setComponents((prevComponents) => {
            const { x: adjustedX, y: adjustedY } = preventComponentOverlap(
                prevComponents, id, x, y, dimensions
            );

            return prevComponents.map((comp) =>
                comp.id === id ? { ...comp, x: adjustedX, y: adjustedY } : comp
            );
        });
    }, [dimensions, setComponents]);

    const MemoizedComponentRenderer = React.memo(ComponentRenderer);

    const memoizedComponents = useMemo(() => {
        if (currentLayer === 'Верхнеуровневая архитектура') {
            const environmentComponents = components.filter(comp => comp.category_name === 'environment');

            // Вычисляем размеры и позицию для системного компонента
            const systemWidth = 200;
            const systemHeight = 100;
            // Центрируем с учетом панелей
            const systemX = Math.max(350, (dimensions.width - systemWidth) / 2);
            const systemY = Math.max(100, (dimensions.height - systemHeight) / 2);

            const systemComponent = {
                id: 'system',
                name: sandboxName,
                category_name: 'system',
                type_name: 'System',
                x: systemX,
                y: systemY,
                width: systemWidth,
                height: systemHeight,
                description: t('sandbox_canvas.system.description')
            };

            // Распределяем компоненты окружения вокруг системного компонента
            if (environmentComponents.length > 0) {
                const angle = (2 * Math.PI) / environmentComponents.length;
                const radius = Math.min(
                    (dimensions.width - 400) / 3, 
                    (dimensions.height - 300) / 3,
                    250 // максимальный радиус
                );

                environmentComponents.forEach((comp, index) => {
                    const angleRad = angle * index - Math.PI / 2; // Начинаем сверху
                    comp.x = systemX + systemWidth / 2 + radius * Math.cos(angleRad) - iconWidth / 2;
                    comp.y = systemY + systemHeight / 2 + radius * Math.sin(angleRad) - iconHeight / 2;
                    
                    // Убеждаемся, что компоненты не выходят за границы
                    comp.x = Math.max(300, Math.min(comp.x, dimensions.width - iconWidth - 50));
                    comp.y = Math.max(50, Math.min(comp.y, dimensions.height - iconHeight - 150));
                });
            }

            return [
                ...environmentComponents.map(comp => (
                    <MemoizedComponentRenderer
                        key={comp.id}
                        comp={comp}
                        onContextMenu={handleContextMenu}
                        onClick={handleComponentClick}
                        onDragMove={handleDragMove}
                    />
                )),
                <SystemComponent
                    key="system"
                    {...systemComponent}
                    onClick={handleSystemClick}
                />,
            ];
        } else {
            return components.map((comp) => (
                <MemoizedComponentRenderer
                    key={comp.id}
                    comp={comp}
                    onContextMenu={handleContextMenu}
                    onClick={handleComponentClick}
                    onDragMove={handleDragMove}
                />
            ));
        }
    }, [components, currentLayer, sandboxName, dimensions, handleContextMenu, handleComponentClick, handleDragMove, handleSystemClick, t]);

    const memoizedComponentPositions = useMemo(() => {
        return components.reduce((acc, comp) => {
            if (typeof comp.x === 'number' && typeof comp.y === 'number') {
                acc[comp.id] = {
                    x: comp.x,
                    y: comp.y,
                    width: iconWidth,
                    height: iconHeight,
                };
            }
            return acc;
        }, {});
    }, [components]);

    // Функция для получения позиции компонента по id и расчета его центра
    const getComponentPosition = useCallback(
        (id) => {
            const component = components.find((comp) => comp.id === id);
            return component || memoizedComponentPositions[id] || null;
        },
        [components, memoizedComponentPositions]
    );

    // Кэшируем результаты getComponentPosition для всех компонентов
    const positionCache = useMemo(() => {
        const cache = {};
        components.forEach(comp => {
            const pos = getComponentPosition(comp.id);
            if (pos) {
                cache[comp.id] = pos;
            }
        });
        return cache;
    }, [components, getComponentPosition]);

    const memoizedRelations = useMemo(() => {
        if (!relations.length && currentLayer !== 'Верхнеуровневая архитектура') return [];

        if (currentLayer === 'Верхнеуровневая архитектура') {
            // Кэшируем центральные координаты для верхнеуровневой архитектуры
            const centerX = dimensions.width / 2;
            const centerY = dimensions.height / 2;

            return components
                .filter(comp => comp.category_name === 'environment')
                .map((comp) => {
                    const compPos = getComponentPosition(comp.id);
                    if (!compPos) return null;

                    const fromXCenter = compPos.x + (compPos.width || iconWidth) / 2;
                    const fromYCenter = compPos.y + (compPos.height || iconHeight) / 2;

                    const relationKey = `rel_${comp.id}_system`;

                    return (
                        <React.Fragment key={relationKey}>
                            <Line
                                points={[fromXCenter, fromYCenter, centerX, centerY]}
                                stroke="transparent"
                                strokeWidth={6}
                                onMouseEnter={(e) => handleMouseEnter(e, { description: t('sandbox_canvas.relation.description', {name: comp.name}) })}
                                onMouseLeave={handleMouseLeave}
                            />
                            <Line
                                points={[fromXCenter, fromYCenter, centerX, centerY]}
                                stroke="gray"
                                strokeWidth={1}
                                dash={[5, 5]}
                                dashOffset={dashOffset}
                            />
                        </React.Fragment>
                    );
                })
                .filter(Boolean);
        } else {
            return relations
                .map((relation) => {
                    const fromPos = positionCache[relation.from_component];
                    const toPos = positionCache[relation.to_component];

                    if (!fromPos || !toPos) return null;

                    const fromXCenter = fromPos.x + (fromPos.width || iconWidth) / 2;
                    const fromYCenter = fromPos.y + (fromPos.height || iconHeight) / 2;
                    const toXCenter = toPos.x + (toPos.width || iconWidth) / 2;
                    const toYCenter = toPos.y + (toPos.height || iconHeight) / 2;

                    const relationKey = `rel_${relation.id || `${relation.from_component}_${relation.to_component}`}`;

                    return (
                        <React.Fragment key={relationKey}>
                            <Line
                                points={[fromXCenter, fromYCenter, toXCenter, toYCenter]}
                                stroke="transparent"
                                strokeWidth={6}
                                onMouseEnter={(e) => handleMouseEnter(e, relation)}
                                onMouseLeave={handleMouseLeave}
                            />
                            <Line
                                points={[fromXCenter, fromYCenter, toXCenter, toYCenter]}
                                stroke="gray"
                                strokeWidth={1}
                                dash={[5, 5]}
                                dashOffset={dashOffset}
                            />
                        </React.Fragment>
                    );
                })
                .filter(Boolean);
        }
    }, [
        currentLayer,
        components,
        relations,
        dimensions.width,
        dimensions.height,
        dashOffset,
        handleMouseEnter,
        handleMouseLeave,
        getComponentPosition,
        positionCache,
        t
    ]);


    // Проверка размеров после вызова хуков
    if (dimensions.width === 0 || dimensions.height === 0) {
        return null;
    }

    return (
        <div
            onContextMenu={(e) => e.preventDefault()}
        >
            <Stage
                width={dimensions.width}
                height={dimensions.height}
                scaleX={scale}
                scaleY={scale}
                x={position.x}
                y={position.y}
                ref={stageRef}
            >
                <Grid width={dimensions.width} height={dimensions.height}/>
                <Layer>{memoizedRelations}</Layer>
                <Layer>{memoizedComponents}</Layer>
            </Stage>

            {/* Всплывающее сообщение */}
            {tooltip.visible && (
                <div
                    style={{
                        position: 'absolute',
                        top: tooltip.y,
                        left: tooltip.x,
                        background: 'rgba(211, 211, 211, 0.5)',
                        padding: '5px',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                        fontSize: '12px',
                        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.2)',
                    }}
                >
                    {tooltip.description}
                </div>
            )}

            {contextMenu && (
                <ContextMenu
                    position={contextMenu}
                    componentName={contextMenu.componentName}
                    onAction={(action, prompt) => {
                        if (action === 'describe') {
                            setAiAssistantPrompt(prompt);
                        }
                        setContextMenu(null);
                    }}
                />
            )}

            <AIAssistant
                token={token}
                sandboxId={sandboxId}
                userId={userInfo.id}
                language={currentLanguage}
                selectedComponent={activeComponent ? activeComponent.name : null}
                selectedTab={activeInfoTab}
                initialPrompt={aiAssistantPrompt}
                aiTutor={aiTutor}
            />
        </div>
    );
};

export default SandboxCanvas;